/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Add, Visibility, Edit, Delete, TrendingUp } from "@mui/icons-material";
import { getAllAboutStats, deleteAboutStats } from "actions/aboutStats";
import { clearError } from "features/aboutStatsSlice";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { PATHNAME } from "constants/pathnames";
import "./About.scss";
import { BrandButton } from "components/brand";

export const AboutStatsList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { aboutStats, loading, deleteLoading, error } = useSelector(
    (state) => state.aboutStats,
  );
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
  });

  const handleDelete = () => {
    dispatch(deleteAboutStats({ id: deleteDialog.id }));
    setDeleteDialog({ open: false, id: null });
  };

  const handleDeleteClick = (id) => {
    setDeleteDialog({
      open: true,
      id,
    });
  };

  useEffect(() => {
    dispatch(getAllAboutStats());
  }, []);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error]);

  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="about-stats-list">
      <Box className="list-header">
        <Typography variant="h4" component="h1" fontWeight="bold">
          {ADMIN_TEXTS.aboutStats.aboutStats}
        </Typography>
        <BrandButton
          variant="primary"
          startIcon={<Add />}
          onClick={() => navigate(`${PATHNAME.aboutStats}/create`)}
          className="create-button"
        >
          {ADMIN_TEXTS.aboutStats.createStat}
        </BrandButton>
      </Box>

      {error && (
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
      )}

      {aboutStats.length === 0 ? (
        <Card>
          <CardContent>
            <Box className="empty-state">
              <TrendingUp sx={{ fontSize: 64, color: "text.secondary" }} />
              <Typography variant="h6" color="text.secondary">
                {ADMIN_TEXTS.aboutStats.noStats}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ADMIN_TEXTS.aboutStats.noStatsDescription}
              </Typography>
              <BrandButton
                variant="primary"
                startIcon={<Add />}
                onClick={() => navigate(PATHNAME.aboutStats.create)}
                className="create-first-button"
              >
                {ADMIN_TEXTS.aboutStats.createFirstStat}
              </BrandButton>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {aboutStats.map((stat) => (
            <Grid item xs={12} sm={6} md={4} key={stat.id}>
              <Card className="stat-card">
                <CardContent>
                  <Box className="stat-header">
                    <Typography
                      variant="h6"
                      component="h3"
                      className="stat-name"
                    >
                      {stat.name}
                    </Typography>
                    <Box className="stat-actions">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`${PATHNAME.aboutStats}/${stat.id}`)
                        }
                        className="action-button view"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`${PATHNAME.aboutStats}/${stat.id}/edit`)
                        }
                        className="action-button edit"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(stat.id)}
                        disabled={deleteLoading}
                        className="action-button delete"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>

                  <Typography
                    variant="h4"
                    component="div"
                    className="stat-value"
                  >
                    {stat.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>{ADMIN_TEXTS.aboutStats.deleteStat}</DialogTitle>
        <DialogContent>
          <Typography>
            {ADMIN_TEXTS.aboutStats.confirmDelete}?{" "}
            {ADMIN_TEXTS.aboutStats.deleteConfirmMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <BrandButton
            variant="secondary"
            onClick={() => setDeleteDialog({ open: false, id: null })}
            color="primary"
          >
            {ADMIN_TEXTS.common.cancel}
          </BrandButton>
          <BrandButton onClick={handleDelete} variant="error">
            {ADMIN_TEXTS.common.delete}
          </BrandButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
